import { forwardRef, useCallback } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/popover'

import { cn } from '@/lib/utils'
import { type ValueOf } from '@/lib/utils/type'

import { type SELECT_FILTER_OPTION_VALUE_MAP, type SelectFilterOptionValue } from './constants'
import { FilterButton } from './filter/filter-button'
import { FilterContent } from './filter/filter-content'
import { type SelectFilterItem } from './types'

export type SelectFilterProps = {
  className?: string
  disabled?: boolean
  hideCount?: boolean
  buttonText: string
  items: SelectFilterItem[]
  values?: string[]
  onChangeValue?: (options: string[]) => void
} & (
  | {
      showsFilterOption: true
      filterOption: ValueOf<typeof SELECT_FILTER_OPTION_VALUE_MAP> | undefined
      onChangeFilterOption: (option: SelectFilterOptionValue) => void
    }
  | {
      showsFilterOption?: false
      filterOption?: never
      onChangeFilterOption?: never
    }
)

export const SelectFilter = forwardRef<HTMLButtonElement, SelectFilterProps>((props, ref) => {
  const {
    className,
    disabled = false,
    hideCount = false,
    buttonText,
    items,
    values = [],
    showsFilterOption = false,
    filterOption,
    onChangeValue,
    onChangeFilterOption: onChangeSelectOption,
  } = props

  const selectedFilterNames = items
    .filter(({ filterValue }) => values.includes(filterValue))
    .map(({ filterName }) => filterName)

  const handleChangeFilter = useCallback(
    (options: string[]) => {
      onChangeValue?.(options)
    },
    [onChangeValue]
  )

  const handleChangeSelectOption = useCallback(
    (option: SelectFilterOptionValue) => {
      onChangeSelectOption?.(option)
    },
    [onChangeSelectOption]
  )

  return (
    <Popover>
      <PopoverTrigger disabled={disabled} asChild>
        <FilterButton
          ref={ref}
          className={cn(className)}
          displayValues={selectedFilterNames}
          filterOption={filterOption}
          disabled={disabled}
          buttonText={buttonText}
        />
      </PopoverTrigger>

      <PopoverContent className={cn('w-fit min-w-[200px] p-0')} align="start" side="bottom">
        <FilterContent
          disabled={disabled}
          hideCount={hideCount}
          items={items}
          values={values}
          showsFilterOption={showsFilterOption}
          filterOption={filterOption}
          onChangeFilter={handleChangeFilter}
          onChangeSelectOption={handleChangeSelectOption}
        />
      </PopoverContent>
    </Popover>
  )
})
SelectFilter.displayName = 'SelectFilter'
